// react
import { useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

// mui
import { Box, CircularProgress } from '@mui/material';

// components
import { MemberActionsPage } from '@karehero/llama/pwa/pages/MemberActionsPage/MemberActionsPage';
import { ActionStatusFilter } from '@karehero/llama/pwa/molecules/ActionStatusTabs/ActionStatusTabs';

// hooks / store / router
import { useCareCircle } from 'hooks';
import { useMemberActions } from 'hooks/useMemberActions';
import { useGetCalendarForCareCircleQuery } from 'store/api/organise';
import { DYNAMIC_ROUTES } from 'router/routes';

const Loader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
    }}
  >
    <CircularProgress />
  </Box>
);

/**
 * MemberActions is the carer-facing actions list + detail/form dialogs for a
 * single care circle, scoped by URL (care-circle + calendar id). Always shows
 * the active care circle — redirects there when the URL points elsewhere.
 */
export const MemberActions = () => {
  const { careCircleId, calendarId } = useParams();
  const { careCircleId: activeCareCircleId } = useCareCircle();

  const { data: activeCalendar } = useGetCalendarForCareCircleQuery(
    activeCareCircleId ?? '',
    { skip: !activeCareCircleId },
  );

  const [tab, setTab] = useState<ActionStatusFilter>('todo');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('action');
  const [editChoiceId, setEditChoiceId] = useState<string | null>(null);

  const {
    isLoading,
    entries,
    changeStatus,
    createAction,
    editAction,
    buildDetail,
    formDialog,
  } = useMemberActions(careCircleId ?? '');

  // Always show the active care circle: redirect when the URL is missing ids or
  // points at a different circle.
  if (
    activeCareCircleId &&
    activeCalendar &&
    (careCircleId !== activeCareCircleId || calendarId !== activeCalendar.id)
  ) {
    return (
      <Navigate
        to={DYNAMIC_ROUTES.V2_ACTIONS(activeCareCircleId, activeCalendar.id)}
        replace
      />
    );
  }

  if (!careCircleId || !calendarId || isLoading) return <Loader />;

  const openAction = (id: string) => {
    searchParams.set('action', id);
    setSearchParams(searchParams, { replace: true });
  };

  const closeAction = () => {
    searchParams.delete('action');
    setSearchParams(searchParams, { replace: true });
  };

  const detailProps = selectedId ? buildDetail(selectedId) : null;
  const selectedEntry = entries.find((e) => e.id === selectedId);

  const handleEdit = () => {
    if (!selectedId) return;
    // Repeating series → ask occurrence vs series; single → straight to edit.
    if (selectedEntry?.recurringEventId) {
      setEditChoiceId(selectedId);
    } else {
      editAction(selectedId);
    }
  };

  return (
    <MemberActionsPage
      tab={tab}
      onTabChange={setTab}
      entries={entries}
      onStatusChange={changeStatus}
      onEntryClick={openAction}
      onCreateAction={createAction}
      detail={
        detailProps && {
          ...detailProps,
          onClose: closeAction,
          onEdit: handleEdit,
        }
      }
      form={formDialog}
      recurrenceChoice={
        editChoiceId
          ? {
              onClose: () => setEditChoiceId(null),
              onEditOccurrence: () => {
                editAction(editChoiceId);
                setEditChoiceId(null);
              },
              onEditSeries: () => {
                editAction(editChoiceId, { editSeries: true });
                setEditChoiceId(null);
              },
            }
          : null
      }
    />
  );
};

MemberActions.displayName = 'MemberActions';
export default MemberActions;
